import { useEffect, useState } from 'react';
import { PrimaryButton } from './PrimaryButton';
import {
  proTwofaReenrollBegin,
  proTwofaReenrollVerify,
} from '../../lib/professional-api';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

// Self-service "change authenticator" modal for the pro account-settings
// page. Fetches a fresh QR on open, the pro scans it with the new device and
// confirms a code. The backend keeps the old authenticator working until a
// new code is confirmed, and an abandoned re-enrol just falls back to
// first-time setup on next login — so this can't lock the pro out.
export function ProTwoFactorModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setQrDataUrl('');
    setSecret('');
    setCode('');
    setCodeError('');
    setLoading(true);
    proTwofaReenrollBegin()
      .then((res) => {
        if (cancelled) return;
        setQrDataUrl(res.qr_data_url ?? '');
        setSecret(res.secret ?? '');
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(apiErrorMessage(err, 'Could not start setup.'));
        onClose();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const confirm = async () => {
    const trimmed = code.replace(/\s+/g, '');
    if (!/^\d{6}$/.test(trimmed)) {
      setCodeError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setBusy(true);
    setCodeError('');
    try {
      const res = await proTwofaReenrollVerify(trimmed);
      toast.success(res.message ?? 'Two-factor authentication updated.');
      onClose();
    } catch (err) {
      setCodeError(apiErrorMessage(err, 'That code could not be verified.'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-[420px] rounded-2xl bg-white p-6 shadow-xl">
        <div className="text-center mb-5">
          <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[22px] mb-1">
            Change authenticator
          </h2>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px]">
            Scan the new QR code with your authenticator app, then enter the
            6-digit code to switch devices.
          </p>
        </div>

        {loading ? (
          <p className="text-center text-[14px] text-[#908e8e] py-8">Loading…</p>
        ) : (
          <div className="flex flex-col gap-5">
            {qrDataUrl ? (
              <div className="flex flex-col items-center">
                <img
                  src={qrDataUrl}
                  alt="2FA QR code"
                  className="h-40 w-40 rounded-lg border border-[#eee]"
                />
                {secret ? (
                  <p className="mt-2 text-center text-[12px] text-[#908e8e]">
                    Manual key:{' '}
                    <span className="font-mono text-[13px] tracking-wider text-[#3a3335] break-all">
                      {secret}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : null}

            <div>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.replace(/[^\d]/g, ''));
                  if (codeError) setCodeError('');
                }}
                className="w-full rounded-[10px] border border-[#eee] px-4 py-3 text-center text-[22px] tracking-[0.5em] text-[#3a3335] focus:border-[#8e2a2f] focus:outline-none"
              />
              {codeError ? (
                <p className="mt-2 text-[13px] text-[#dc2626]">{codeError}</p>
              ) : null}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="flex-1 rounded-[10px] border border-[#eee] py-3 font-['DM_Sans',sans-serif] font-bold text-[14px] text-[#3a3335] hover:bg-[#f9f7f2] disabled:opacity-50"
              >
                Cancel
              </button>
              <PrimaryButton
                onClick={confirm}
                className="flex-1 py-3"
                disabled={busy}
              >
                {busy ? 'Verifying…' : 'Verify & save'}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
